import { page } from '$app/stores';

import { get } from 'svelte/store';

import { parseSlug } from '@shared/utils';

import type { ArtworksFiltersOptionsResponse, FormOptions, FormValues, ArtworksFiltersModel } from '../types';
import { defaultsData } from './data';
import { ArtworkViewMode } from './options';

export function getOptions(data: ArtworksFiltersOptionsResponse): FormOptions {
  return {
    artists: data.artists
      .filter(({ name, id, slug }) => name && id && slug)
      .map((artist) => ({
        text: artist.name as NonNullable<typeof artist.name>,
        value: artist.slug as NonNullable<typeof artist.slug>,
        count: artist.art_assets_count,
      }))
      .sort((a, b) => a.text.localeCompare(b.text)),

    curators: data.curators
      .filter(({ name, id, slug }) => name && id && slug)
      .map((curator) => ({
        text: curator.name as NonNullable<typeof curator.name>,
        value: curator.slug as NonNullable<typeof curator.slug>,
        count: curator.art_assets_count,
      }))
      .sort((a, b) => a.text.localeCompare(b.text)),

    styles: data.styles.map(({ name, slug, art_assets_count }) => ({
      text: name,
      value: slug,
      count: art_assets_count,
    })),
    medium: data.medium.map(({ name, slug, art_assets_count }) => ({
      text: name,
      value: slug,
      count: art_assets_count,
    })),
    countries: data.countries.map(({ name, slug, art_assets_count }) => ({
      text: name,
      value: slug,
      count: art_assets_count,
    })),
    token_status: data.token_status.map((status) => ({ text: status, value: status })),
    price: data.price || [0, 0],
    creation_year: data.creation_year || [0, 0],
    original_size_x: data.original_size_x || [0, 0],
    original_size_y: data.original_size_y || [0, 0],
  };
}

export function getValues(data: ArtworksFiltersOptionsResponse): FormValues {
  return {
    artists_slugs: data.artists.map((artist) => ({
      text: artist.name as NonNullable<typeof artist.name>,
      value: artist.slug as NonNullable<typeof artist.slug>,
    })),
    curators_slugs: data.curators.map((curator) => ({
      text: curator.name as NonNullable<typeof curator.name>,
      value: curator.slug as NonNullable<typeof curator.slug>,
    })),
    styles_slugs: data.styles.map(({ slug, name }) => ({ text: name, value: slug })),
    medium_slugs: data.medium.map(({ slug, name }) => ({ text: name, value: slug })),
    countries_slugs: data.countries.map(({ slug, name }) => ({ text: name, value: slug })),
    token_status: data.token_status.includes(ArtworkViewMode.OnSale) ? ArtworkViewMode.OnSale : ArtworkViewMode.All,
    price: data.price || [0, 0],
    // curator_review: data.curator_review || false,
    creation_year: data.creation_year || [0, 0],
    original_size_x: data.original_size_x || [0, 0],
    original_size_y: data.original_size_y || [0, 0],
  };
}

export function getValueFromSlug(slug: string) {
  const params = parseSlug(slug);

  if (!params['token_status']) {
    params['token_status'] = ArtworkViewMode.All;
  }

  const paramsValues: Record<string, string | string[] | number[] | boolean> = {};
  const defaultValues = defaultsData.values as { [key: string]: any };

  Object.entries(params).forEach(([key, value]) => {
    if (!(key in defaultValues)) return;

    switch (key) {
      case 'price':
      case 'creation_year':
      case 'original_size_x':
      case 'original_size_y':
        paramsValues[key] = value.split(',').map(Number);
        break;
      case 'styles_slugs':
      case 'medium_slugs':
      case 'countries_slugs':
      case 'artists_slugs':
      case 'curators_slugs':
        paramsValues[key] = value.split(',');
        break;
      case 'token_status':
        paramsValues[key] = value;
        break;
      case 'curator_review':
        paramsValues[key] = value === 'true';
        break;
      default: {
        paramsValues[key] = value;
      }
    }
  });

  return paramsValues;
}

export function toQueryStringParams(values?: FormValues, initialValues?: FormValues): string {
  const params = new URLSearchParams();

  params.append('institutions', get(page).data.configurations.id);

  if (!values || !initialValues) {
    // if no values or no initialValues still fetch current institution instead of all institutions data
    return params.toString();
  }

  if (values.artists_slugs.length) {
    params.append('artists_slugs', values.artists_slugs.map((artist) => artist.value).join(','));
  }

  if (values.curators_slugs.length) {
    params.append('curators_slugs', values.curators_slugs.map((curator) => curator.value).join(','));
  }

  if (values.styles_slugs.length) {
    params.append('styles_slugs', values.styles_slugs.map((style) => style.value).join(','));
  }

  if (values.medium_slugs.length) {
    params.append('medium_slugs', values.medium_slugs.map((medium) => medium.value).join(','));
  }

  if (values.countries_slugs.length) {
    params.append('countries_slugs', values.countries_slugs.map((country) => country.value).join(','));
  }

  if (values.token_status !== ArtworkViewMode.All) {
    params.append('token_status', values.token_status);
  }

  if (values.curator_review) {
    params.append('curator_review', values.curator_review.toString());
  }

  if (
    values.price &&
    initialValues.price &&
    (values.price[0] !== initialValues.price[0] || values.price[1] !== initialValues.price[1])
  ) {
    params.append('price', values.price.join(','));
  }

  if (
    values.creation_year &&
    initialValues.creation_year &&
    (values.creation_year[0] !== initialValues.creation_year[0] ||
      values.creation_year[1] !== initialValues.creation_year[1])
  ) {
    params.append('creation_year', values.creation_year.join(','));
  }

  if (
    values.original_size_x &&
    initialValues.original_size_x &&
    (values.original_size_x[0] !== initialValues.original_size_x[0] ||
      values.original_size_x[1] !== initialValues.original_size_x[1])
  ) {
    params.append('original_size_x', values.original_size_x.join(','));
  }

  if (
    values.original_size_y &&
    initialValues.original_size_y &&
    (values.original_size_y[0] !== initialValues.original_size_y[0] ||
      values.original_size_y[1] !== initialValues.original_size_y[1])
  ) {
    params.append('original_size_y', values.original_size_y.join(','));
  }

  return params.toString();
}

export function toSlug(values: FormValues, initialValues: FormValues): string {
  let slug = '';

  if (values.token_status !== ArtworkViewMode.All) {
    slug += `/token_status-${values.token_status.replaceAll(',', '+')}`;
  }

  if (values.artists_slugs.length) {
    slug += `/artists-${values.artists_slugs.map((artist) => artist.value).join('+')}`;
  }

  if (values.curators_slugs.length) {
    slug += `/curators-${values.curators_slugs.map((curator) => curator.value).join('+')}`;
  }

  if (values.styles_slugs.length) {
    slug += `/styles-${values.styles_slugs.map((style) => style.value).join('+')}`;
  }

  if (values.medium_slugs.length) {
    slug += `/medium-${values.medium_slugs.map((medium) => medium.value).join('+')}`;
  }

  if (values.countries_slugs.length) {
    slug += `/countries-${values.countries_slugs.map((country) => country.value).join('+')}`;
  }

  if (values.curator_review) {
    slug += `/curator_review-${values.curator_review}`;
  }

  if (
    values.price &&
    initialValues.price &&
    (values.price[0] !== initialValues.price[0] || values.price[1] !== initialValues.price[1])
  ) {
    slug += `/price-${values.price.join('+')}`;
  }

  if (
    values.creation_year &&
    initialValues.creation_year &&
    (values.creation_year[0] !== initialValues.creation_year[0] ||
      values.creation_year[1] !== initialValues.creation_year[1])
  ) {
    slug += `/creation_year-${values.creation_year.join('+')}`;
  }

  if (
    values.original_size_x &&
    initialValues.original_size_x &&
    (values.original_size_x[0] !== initialValues.original_size_x[0] ||
      values.original_size_x[1] !== initialValues.original_size_x[1])
  ) {
    slug += `/original_size_x-${values.original_size_x.join('+')}`;
  }

  if (
    values.original_size_y &&
    initialValues.original_size_y &&
    (values.original_size_y[0] !== initialValues.original_size_y[0] ||
      values.original_size_y[1] !== initialValues.original_size_y[1])
  ) {
    slug += `/original_size_y-${values.original_size_y.join('+')}`;
  }

  return slug;
}

export function getUpdatedOptions(
  response: ArtworksFiltersOptionsResponse,
  currentOption: ArtworksFiltersModel['currentOption'],
): FormOptions {
  const options: FormOptions = getOptions(response);
  const key = Object.keys(currentOption)[0];
  const currentOptionLength = currentOption[key as keyof FormOptions]?.length || 0;

  if (!key) return options;
  if (JSON.stringify(options[key as keyof FormOptions]) === JSON.stringify(currentOption[key as keyof FormOptions]))
    return options;

  if (key === 'artists' && options.artists.length > currentOptionLength) {
    return { ...options, artists: options.artists };
  }

  if (key === 'styles' && options.styles.length > currentOptionLength) {
    return { ...options, styles: options.styles };
  }

  if (key === 'medium' && options.medium.length > currentOptionLength) {
    return { ...options, medium: options.medium };
  }

  if (key === 'countries' && options.countries.length > currentOptionLength) {
    return { ...options, countries: options.countries };
  }

  if (key === 'token_status' && options.token_status.length > currentOptionLength) {
    return { ...options, token_status: options.token_status };
  }

  if (
    key === 'price' &&
    options.price[0] > 0 &&
    options.price[1] > 0 &&
    currentOption.price &&
    (options.price[0] < currentOption.price[0] || options.price[1] > currentOption.price[1])
  ) {
    return { ...options, price: options.price };
  }

  return {
    ...options,
    ...currentOption,
  };
}
